import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  ArrowLeft,
  Phone,
  Mic,
  Bike,
  Copy,
  MapPin,
  CheckCircle,
  ArrowRight,
  Check,
  ChevronDown,
  ChevronUp,
  ShieldCheck,
  ArrowDown,
  HelpCircle,
  ClipboardList,
  ThumbsUp,
  Bell,
  Truck,
  X,
  Maximize2,
} from "lucide-react";

import { MapContainer, TileLayer, Marker, Polyline } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

import banner1 from "../Assets/banner_1.webp";
import banner2 from "../Assets/banner_2.webp";
import item1 from "../Assets/1 (2).jpeg";
import item2 from "../Assets/1 (4).jpeg";
import driver from "../Assets/1 (6).jpeg";

export default function OrderTracking() {
  const images = [banner1, banner2];
  const mapRef = useRef(null);

  const orderId = "#7418529630";

  const products = [
    {
      id: 1,
      name: "Amul Taaza Milk 1L",
      qty: 1,
      price: 60,
      image: item1,
    },
    {
      id: 2,
      name: "Britannia Brown Bread",
      qty: 1,
      price: 45,
      image: item2,
    },
  ];

  const [copied, setCopied] = useState(false);
  const [open, setOpen] = useState(false);
  const [index, setIndex] = useState(0);
  const [showSafety, setShowSafety] = useState(false);
  const [showBigMap, setShowBigMap] = useState(false);
  const [isScrollExpanded, setIsScrollExpanded] = useState(false);

  const baseImages = [banner1, banner2];

  const loopedImages = Array.from({ length: 40 }, (_, i) => {
    return baseImages[i % baseImages.length];
  });

  const iconMap = {
    Bike: <Bike size={18} />,
    MapPin: <MapPin size={18} />,
    Phone: <Phone size={18} />,
  };

  const driverData = {
    name: "Shivam",
    message: "I have picked up your order, and I am on the way",
    statusColor: "text-orange-600",
    image: driver,
  };

  const deliveryDetails = {
    header: {
      title: "Your delivery details",
      subtitle: "Details of your current order",
      icon: "Bike",
    },
    sections: [
      {
        id: "address",
        title: "Delivery at Work",
        description:
          "B 218 Ithum, Second Floor, Tower, Industrial Area, Sector 62, Noida, Uttar Pradesh, 210307",
        icon: "MapPin",
      },
      {
        id: "contact",
        title: "Shivam, 84049*****",
        icon: "Phone",
      },
    ],
  };

  /* ---------------- SCROLL LOGIC ---------------- */
  useEffect(() => {
    const onScroll = () => {
      if (!showBigMap) {
        setIsScrollExpanded(window.scrollY > 360);
      }
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [showBigMap]);

  /* ---------------- CAROUSEL ---------------- */
  useEffect(() => {
    const interval = setInterval(() => {
      setIndex((prev) => (prev + 1) % loopedImages.length);
    }, 4000);

    return () => clearInterval(interval);
  }, [loopedImages.length]);

  const total = useMemo(
    () => products.reduce((sum, item) => sum + item.price * item.qty, 0),
    [products]
  );

  const handleCopy = () => {
    navigator.clipboard.writeText(orderId);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  /* ---------------- LEAFLET ---------------- */
  const homeLat = 28.61;
  const homeLng = 77.2;
  const driverLat = 28.605;
  const driverLng = 77.215;

  const mapCenter = [(homeLat + driverLat) / 2, (homeLng + driverLng) / 2];

  const homeIcon = new L.Icon({
    iconUrl:
      "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-yellow.png",
    shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
    iconSize: [25, 41],
    iconAnchor: [12, 41],
  });

  const driverIcon = new L.Icon({
    iconUrl: "http://bumppy.in/packky/api/v1/uploads/images/driver_icon.png",
    iconSize: [30, 30],
    iconAnchor: [15, 30],
  });

  useEffect(() => {
    if (!mapRef.current) return;
    requestAnimationFrame(() => {
      mapRef.current.invalidateSize();
    });
  }, [showBigMap, isScrollExpanded]);

  return (
    <div className="min-h-screen bg-gray-100 flex justify-center">
      <div className="w-full max-w-md bg-gray-100">
        {/* HEADER */}
        <div className="bg-orange-500 text-white px-4 py-3 sticky top-0 z-50">
          <div className="flex items-center gap-3">
            <button className="p-1 rounded-full bg-orange-500">
              <ArrowLeft size={18} />
            </button>
            <div className="flex-1 text-center">
              <p className="text-sm">Order is on the way</p>
              <h1 className="text-lg font-semibold">Arriving in 10 minutes</h1>
            </div>
          </div>
        </div>

        {/* CAROUSEL */}
        <div className="w-full max-w-md mx-auto mt-5">
          <div className="overflow-hidden ">
            <div
              className="flex transition-transform duration-700 ease-in-out"
              style={{
                transform: `translateX(-${index * 100}%)`,
              }}
            >
              {loopedImages.map((img, i) => (
                <img
                  key={i}
                  src={img}
                  alt={`slide-${i}`}
                  className="w-full h-70 object-cover flex-shrink-0"
                />
              ))}
            </div>
          </div>

          {/* <div className="flex justify-center gap-2 mt-4">
            {baseImages.map((_, i) => (
              <button
                key={i}
                onClick={() => setIndex(i)}
                className={`w-2.5 h-2.5 rounded-full transition-colors ${
                  index % baseImages.length === i
                    ? "bg-orange-600"
                    : "bg-gray-300"
                }`}
              />
            ))}
          </div> */}
        </div>

        {/* MAP SECTION */}
        <div
          className={`relative max-w-md mx-auto transition-all duration-500 z-40
             ${isScrollExpanded ? "pt-56" : "pt-5"}
        `}
        >
          <div className="bg-white shadow rounded-xl">
            {/* MAP WRAPPER */}
            <div
              className={`
                 relative mt-2
                  transition-[height] duration-500 ease-out
                  ${isScrollExpanded ? "h-64" : "h-44"}
                `}
            >
              <MapContainer
                center={mapCenter}
                zoom={13}
                className="h-full w-full rounded-t-xl z-10"
                scrollWheelZoom={isScrollExpanded}
                dragging={isScrollExpanded}
                zoomControl={isScrollExpanded}
                whenCreated={(map) => (mapRef.current = map)}
              >
                <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />

                <Marker position={[driverLat, driverLng]} icon={driverIcon} />
                <Marker position={[homeLat, homeLng]} icon={homeIcon} />

                <Polyline
                  positions={[
                    [driverLat, driverLng],
                    [homeLat, homeLng],
                  ]}
                  pathOptions={{ color: "blue", weight: 4 }}
                />
              </MapContainer>

              <button
                onClick={() => setIsScrollExpanded(!isScrollExpanded)}
                className={`absolute z-50 bg-white p-2 rounded-full shadow-md
                  ${isScrollExpanded ? "top-2 right-2" : "bottom-2 right-2"}
             `}
              >
                {isScrollExpanded ? (
                  <X className="w-5 h-5 text-gray-700" />
                ) : (
                  <Maximize2 className="w-5 h-5 text-gray-700" />
                )}
              </button>
            </div>

            {/* INFO CARD */}
          
              <div className="bg-white relative z-0 rounded-br-xl rounded-bl-xl">
                <div className="p-5 flex items-start gap-3">
                  <div className="p-2 rounded-full bg-gray-100">
                    <Truck className="w-6 h-6 text-orange-500" />
                  </div>
                  <div>
                    <h3 className="font-semibold">Your order is confirmed</h3>
                    <p className="text-sm text-gray-600">
                      On the way to your location
                    </p>
                  </div>
                </div>
              </div>
            
          </div>
        </div>

        {/* DELIVERY PARTNER */}
        <div className="bg-white max-w-md mt-4 rounded-xl shadow p-4 mx-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full overflow-hidden bg-gray-200">
              <img
                src={driverData.image}
                alt="Driver"
                className="w-full h-full object-cover"
              />
            </div>

            <div className="flex-1">
              <p className="font-semibold">
                I'm {driverData.name}, your delivery partner
              </p>
              <p className={`text-sm ${driverData.statusColor}`}>
                {driverData.message}
              </p>
            </div>

            <button className="p-2 rounded-full border">
              <Phone size={18} className="text-orange-600" />
            </button>
          </div>
        </div>

        {/* TIP SECTION */}
        {/* <div className="bg-white max-w-md mt-4 rounded-xl p-4">
          <h3 className="font-semibold mb-1">
            Delivering happiness at your doorstep!
          </h3>
          <p className="text-sm text-gray-500 mb-3">
            Thank them by leaving a tip
          </p>

          <div className="flex gap-3 overflow-x-auto">
            {["₹20", "₹30", "₹50", "Other"].map((tip) => (
              <button
                key={tip}
                className="min-w-[70px] px-4 py-2 rounded-full border text-sm font-medium hover:bg-orange-50 hover:border-orange-500"
              >
                {tip}
              </button>
            ))}
          </div>
        </div> */}

        {/* STORE DISTANCE */}
        <div className="bg-white max-w-md mt-4 mx-4 rounded-xl p-4">
          {/* Top Row */}
          <div className="flex items-start gap-3">
            <div className="p-2 rounded-full bg-gray-100">
              <ShieldCheck className="w-6 h-6 text-green-500" />
            </div>

            <p className="text-sm text-gray-700 flex-1">
              Your Anlook store is <strong>700 mts away.</strong>
              <br />
              <span className="text-orange-600">
                Learn about delivery partner safety
              </span>
            </p>

            <button
              onClick={() => setShowSafety(!showSafety)}
              className="p-3 rounded-full ml-auto bg-gray-100"
            >
              {showSafety ? <ArrowDown size={18} /> : <ArrowRight size={18} />}
            </button>
          </div>

          {/* Safety Details */}
          {showSafety && (
            <div className="mt-4 bg-gray-50 rounded-lg p-3 text-sm text-gray-700">
              <ul className="space-y-2">
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-[2px]" />
                  <span>Delivery partners are verified & trained</span>
                </li>

                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-[2px]" />
                  <span>Live order tracking available</span>
                </li>

                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-[2px]" />
                  <span>Contactless delivery option</span>
                </li>

                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-[2px]" />
                  <span>Emergency support during delivery</span>
                </li>
              </ul>
            </div>
          )}
        </div>

        {/* DELIVERY INSTRUCTIONS */}
        <div className="bg-white max-w-md mt-4 mx-4 rounded-xl p-4 flex items-start gap-3">
          <div className="p-2 rounded-full bg-gray-100">
            <Mic size={18} />
          </div>

          <div className="flex-1">
            <p className="font-semibold">Add delivery instructions</p>
            <p className="text-sm text-gray-500">
              Help your delivery partner reach you
            </p>
          </div>

          <button
            onClick={() => setOpen(!open)}
            className="p-3 rounded-full bg-gray-100"
          >
            {open ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
          </button>
        </div>

        {open && (
          <div className="bg-white max-w-md mt-2 mb-6 mx-4 rounded-xl shadow p-4 space-y-4">
            {/* HEADER */}
            <div className="flex items-start gap-4">
              <div className="p-3 rounded-full bg-gray-100">
                {iconMap[deliveryDetails.header.icon]}
              </div>
              <div>
                <p className="font-semibold">{deliveryDetails.header.title}</p>
                <p className="text-sm text-gray-500">
                  {deliveryDetails.header.subtitle}
                </p>
              </div>
            </div>

            <hr />

            {/* SECTIONS */}
            {deliveryDetails.sections.map((item, index) => (
              <div key={item.id}>
                <div className="flex items-start gap-3">
                  <div className="p-2 rounded-full bg-gray-100">
                    {iconMap[item.icon]}
                  </div>
                  <div>
                    <p className="font-semibold">{item.title}</p>
                    {item.description && (
                      <p className="text-sm text-gray-600">
                        {item.description}
                      </p>
                    )}
                  </div>
                </div>

                {index !== deliveryDetails.sections.length - 1 && (
                  <hr className="mt-4" />
                )}
              </div>
            ))}
          </div>
        )}

        {/* need help */}
        <div className="bg-white max-w-md mt-4 mb-6 mx-4 rounded-xl shadow p-4">
          <div className="flex items-start gap-4">
            <div className="p-3 rounded-full bg-gray-100">
              <HelpCircle size={18} />
            </div>

            <div>
              <p className="font-semibold mb-1">Need help?</p>
              <p className="text-sm text-gray-500 mb-2">
                chat with us about any issue related to your order
              </p>
            </div>
            <button className="p-3 rounded-full ml-auto bg-gray-100">
              <ArrowRight size={18} />
            </button>
          </div>
        </div>

        {/* order summary */}
        <div className="bg-white max-w-md mt-4 mb-6 mx-4 rounded-xl shadow p-4 space-y-4">
          <div className="flex items-start gap-4">
            <div className="p-3 rounded-full bg-gray-100">
              <ClipboardList size={18} />
            </div>

            <div className="flex-1">
              <p className="font-semibold">Order Summary</p>

              <div className="flex items-center gap-2 mt-1">
                <p className="text-sm text-gray-500">Order ID: {orderId}</p>

                <button
                  onClick={handleCopy}
                  className="p-1 rounded hover:bg-gray-100"
                >
                  {copied ? (
                    <Check size={16} className="text-orange-600" />
                  ) : (
                    <Copy size={16} className="text-gray-600" />
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* VIEW BUTTON */}
          <button
            onClick={() => setOpen(!open)}
            className="w-full flex items-center justify-center px-4 py-3 gap-2 rounded-lg border text-sm font-medium hover:bg-gray-50"
          >
            <span>View Order Summary</span>
            {open ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
          </button>

          {open && (
            <div className="space-y-4">
              {/* PRODUCTS */}
              <div className="space-y-3">
                {products.map((item) => (
                  <div key={item.id} className="flex items-center gap-3">
                    <img
                      src={item.image}
                      alt={item.name}
                      className="w-14 h-14 rounded-lg border object-cover"
                    />

                    <div className="flex-1">
                      <p className="text-sm font-medium">{item.name}</p>
                      <p className="text-xs text-gray-500">Qty: {item.qty}</p>
                    </div>

                    <p className="text-sm font-semibold">
                      ₹{item.price * item.qty}
                    </p>
                  </div>
                ))}
              </div>

              <hr />

              {/* PAYMENT DETAILS */}
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span>Item total</span>
                  <span>₹{total}</span>
                </div>
                <div className="flex justify-between">
                  <span>Delivery charge</span>
                  <span className="text-orange-600">FREE</span>
                </div>
                <div className="flex justify-between font-semibold">
                  <span>Paid via UPI</span>
                  <span>₹{total}</span>
                </div>
              </div>

              <hr />

              <div className="text-sm text-orange-600 font-medium">
                Payment successful • Order confirmed
              </div>
            </div>
          )}
        </div>

        {/* like service */}
        <div className="bg-white max-w-md mt-4 mb-6 mx-4 rounded-xl p-4 pb-0">
          <div className="flex items-start gap-4 mb-4">
            <div className="p-3 rounded-full bg-gray-100">
              <ThumbsUp size={18} />
            </div>

            <div>
              <p className="font-semibold mb-1">Do you like Our Service?</p>
              <p className="text-sm text-gray-500">
                Do rate us on the Play Store if you are enjoying our service.
                you can rate again if you have already rated us.
              </p>
            </div>
          </div>

          <div className="-mx-4 bg-gray-100 pt-[3px] flex gap-[3px]">
            <button className="w-1/2 py-3 text-sm text-orange-500 font-medium bg-white rounded-bl-xl">
              May be later
            </button>

            <button className="w-1/2 py-3 text-sm text-orange-500 font-medium bg-white rounded-br-xl">
              Rate Anlook
            </button>
          </div>
        </div>

        {/* up to date */}
        <div className="bg-white max-w-md mt-4 mb-6 mx-4 rounded-xl p-4 pb-0">
          <div className="flex items-start gap-4 mb-4">
            <div className="p-3 rounded-full bg-gray-100">
              <Bell size={18} />
            </div>

            <div>
              <p className="font-semibold mb-1">Want to stay up-to-date?</p>
              <p className="text-sm text-gray-500">
                Enable notification so that we can notify you about your order
                and amazing offers.
              </p>
            </div>
          </div>

          <div className="flex gap-3 bg-gray-100 -mx-4 pt-[3px] ">
            <button className="w-full py-3 text-sm font-medium text-center text-orange-500 bg-white rounded-bl-xl rounded-br-xl">
              Enable
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
